import {
  Card,
  CardHeader,
  Divider,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
  CardActions,
  Button,
} from "@mui/material";
import { useSnackbar } from "../../../context/SnackbarContext";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useUpdatePassword } from "../../../api/user/use-update-pass";
import { logout } from "../../../features/slices/Auth";
import { AppDispatch } from "../../../store";
import { checkPasswordStrength } from "../../../utils/util";

export function UpdatePasswordForm() {
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const updatePasswordMutation = useUpdatePassword();
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch<AppDispatch>();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const error = checkPasswordStrength(password);
    if (error) {
      setPasswordError(error);
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    if (!oldPassword) {
      setPasswordError("Please enter your current password.");
      return;
    }

    setPasswordError(null);
    const handleLogout = async () => {
      try {
        const resultAction = await dispatch(logout());
        if (resultAction.payload) {
          showSnackbar("Logout successful!", "success");
        } else {
          showSnackbar("Logout failed.", "error");
        }
      } catch (error) {
        showSnackbar("Logout failed.", "error");
        console.error("Failed to logout:", error);
      }
    };
    updatePasswordMutation.mutate(
      {
        old_password: oldPassword,
        password: password,
        confirm_password: confirmPassword,
      },
      {
        onSuccess: () => {
          setSuccessMessage("Password updated successfully.");
          showSnackbar("Password updated successfully.", "success");
          setOldPassword("");
          setPassword("");
          setConfirmPassword("");
          handleLogout();
        },
        onError: (error: any) => {
          const errorMsg =
            error?.response?.data?.message || "Failed to update password.";
          setPasswordError(errorMsg);
          showSnackbar(errorMsg, "error");
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader subheader="Update password" title="Password" />
        <Divider />
        <CardContent>
          <Stack spacing={3} sx={{ maxWidth: "sm" }}>
            <FormControl fullWidth>
              <InputLabel>Current Password</InputLabel>
              <OutlinedInput
                label="Current Password"
                name="old_password"
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>New Password</InputLabel>
              <OutlinedInput
                label="New Password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Confirm New Password</InputLabel>
              <OutlinedInput
                label="Confirm New Password"
                name="confirm_password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </FormControl>
            {passwordError && (
              <Typography color="error" variant="body2">
                {passwordError}
              </Typography>
            )}
            {successMessage && (
              <Typography color="primary" variant="body2">
                {successMessage}
              </Typography>
            )}
          </Stack>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            type="submit"
            disabled={updatePasswordMutation.isLoading}
          >
            {updatePasswordMutation.isLoading ? "Updating..." : "Update"}
          </Button>
        </CardActions>
      </Card>
    </form>
  );
}
